import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const LegalLayout = ({ title, children }) => {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-white text-slate-600">
      <div className="container-x py-24 max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" /> {t("legal.back")}
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">{title}</h1>
        <div className="space-y-5 leading-relaxed text-sm md:text-base [&_h2]:text-slate-900 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-2">
          {children}
        </div>
      </div>
    </div>
  );
};

const useContent = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setC(r.data)).catch(() => {}); }, []);
  return c;
};


export function Impressum() {
  const c = useContent();
  const { lang } = useLang();
  if (!c) return <div className="min-h-screen bg-white" />;
  const company = c.imprint_company || "Autoaufbereitung Berisha";
  const addr = c.imprint_address || c.address;
  const ustLine = c.imprint_ust_id
    ? (lang === "en" ? `VAT identification number pursuant to § 27a of the German VAT Act: ${c.imprint_ust_id}` : `Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG: ${c.imprint_ust_id}`)
    : (lang === "en" ? "Will be added by the owner." : "Wird vom Inhaber ergänzt.");

  if (lang === "en") {
    return (
      <LegalLayout title="Imprint">
        <h2>Information according to § 5 TMG</h2>
        <p>{company}<br />{addr}</p>
        {c.imprint_owner && (<><h2>Represented by</h2><p>{c.imprint_owner}</p></>)}
        <h2>Contact</h2>
        <p>Phone: {c.phone}<br />Email: {c.email}</p>
        <h2>VAT ID</h2>
        <p>{ustLine}</p>
        <h2>Responsible for the content</h2>
        <p>{c.imprint_owner || company}, {addr}</p>
        <h2>Liability for content</h2>
        <p>As a service provider, we are responsible for our own content on these pages in accordance with § 7 (1) TMG. However, we are not obligated to monitor transmitted or stored third-party information.</p>
      </LegalLayout>
    );
  }
  return (
    <LegalLayout title="Impressum">
      <h2>Angaben gemäß § 5 TMG</h2>
      <p>{company}<br />{addr}</p>
      {c.imprint_owner && (<><h2>Vertreten durch</h2><p>{c.imprint_owner}</p></>)}
      <h2>Kontakt</h2>
      <p>Telefon: {c.phone}<br />E-Mail: {c.email}</p>
      <h2>Umsatzsteuer-ID</h2>
      <p>{ustLine}</p>
      <h2>Verantwortlich für den Inhalt</h2>
      <p>{c.imprint_owner || company}, {addr}</p>
      <h2>Haftung für Inhalte</h2>
      <p>Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.</p>
    </LegalLayout>
  );
}

export function Datenschutz() {
  const c = useContent();
  const { lang } = useLang();
  if (!c) return <div className="min-h-screen bg-white" />;
  const company = c.imprint_company || "Autoaufbereitung Berisha";
  const addr = c.imprint_address || c.address;

  if (lang === "en") {
    return (
      <LegalLayout title="Privacy Policy">
        <h2>1. Data protection at a glance</h2>
        <p>The protection of your personal data is important to us. We process your data exclusively on the basis of the statutory provisions (GDPR, BDSG).</p>
        <h2>2. Responsible party</h2>
        <p>{company}, {addr}, Phone: {c.phone}, Email: {c.email}.</p>
        <h2>3. Data collection</h2>
        <p>When you use our contact or appointment form, the data you enter (name, phone number, email, vehicle details, message) is stored to process your request.</p>
        <h2>4. Cookies</h2>
        <p>Our website uses cookies to improve usability. You can object to their use via the cookie banner.</p>
        <h2>5. Your rights</h2>
        <p>You have the right at any time to information, correction, deletion and restriction of the processing of your stored data.</p>
      </LegalLayout>
    );
  }
  return (
    <LegalLayout title="Datenschutzerklärung">
      <h2>1. Datenschutz auf einen Blick</h2>
      <p>Der Schutz Ihrer persönlichen Daten ist uns ein wichtiges Anliegen. Wir verarbeiten Ihre Daten ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, BDSG).</p>
      <h2>2. Verantwortliche Stelle</h2>
      <p>{company}, {addr}, Telefon: {c.phone}, E-Mail: {c.email}.</p>
      <h2>3. Erhebung von Daten</h2>
      <p>Wenn Sie unser Kontakt- oder Terminformular nutzen, werden die von Ihnen eingegebenen Daten (Name, Telefonnummer, E-Mail, Fahrzeugangaben, Nachricht) zur Bearbeitung Ihrer Anfrage gespeichert.</p>
      <h2>4. Cookies</h2>
      <p>Unsere Website verwendet Cookies, um die Benutzerfreundlichkeit zu verbessern. Sie können der Verwendung über den Cookie-Banner widersprechen.</p>
      <h2>5. Ihre Rechte</h2>
      <p>Sie haben jederzeit das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer gespeicherten Daten.</p>
    </LegalLayout>
  );
}
